import React, { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, styled, TextField, useTheme } from '@mui/material';
import { bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import withFormControl from '@/hoc/withFormControl';

import CalendarPopover from './CalendarPopover';
import { parseFormat, prepareDisplayDate, prepareValue } from './utils';

const DATE_ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ssX";

interface DateRangeSelectProps {
  id?: string;
  name: string;
  value: { start: Date | string; end: Date | string };
  onChange?: (range: { start?: Date | string; end?: Date | string }) => void;
  options: string[];
  dateFormat?: string;
}

const StyledStack = styled(Stack)(({ theme }) => ({
  '& .MuiTextField-root .MuiInputBase-root': {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
    ...(theme.direction === 'ltr'
      ? { paddingLeft: theme.spacing(1), paddingRight: 0 }
      : { paddingRight: theme.spacing(1), paddingLeft: 0 }),
    backgroundColor: theme.palette.background.default
  },
  '& .MuiTextField-root .MuiInputBase-root .MuiInputBase-input::placeholder': {
    ...theme.typography.body2,
    color: theme.palette.grey[700]
  },
  '& .MuiTextField-root .MuiInputBase-root fieldset': {
    borderColor: theme.palette.grey[600]
  },
  '& .MuiFormControl-root': {
    padding: 0
  }
}));

const StyledTextField = styled(TextField)({
  '& .MuiInputBase-root': {
    '& .MuiInputBase-input': {
      '&::placeholder': {
        opacity: 1
      }
    }
  }
});

const DateRangePickerField: FC<DateRangeSelectProps> = ({ id, value, onChange, dateFormat = DATE_ISO_FORMAT }) => {
  const { t } = useTranslation('common');
  const [isReadyToClose, setIsReadyToClose] = useState(false);

  const { palette } = useTheme();
  const uniqueId = useMemo(() => `date-range-${crypto.randomUUID()}`, []);
  const popupState = usePopupState({ variant: 'popover', popupId: uniqueId });

  const handleChange = useCallback(
    (startDate: Date, endDate: Date, close: VoidFunction) => {
      const start = prepareValue(startDate, dateFormat);
      const end = prepareValue(endDate, dateFormat);

      onChange?.({ start, end });
      isReadyToClose && close();
      setIsReadyToClose(!isReadyToClose);
    },
    [dateFormat, onChange, isReadyToClose]
  );

  const ranges = useMemo(
    () => [
      {
        startDate: parseFormat(value?.start, dateFormat, true)!,
        endDate: parseFormat(value?.end, dateFormat, true)!,
        key: 'range',
        color: palette.primary[250]!
      }
    ],
    [value, palette, dateFormat]
  );

  return (
    <>
      <StyledStack direction="row" gap={1} {...bindTrigger(popupState)}>
        <StyledTextField
          InputProps={{ id, onFocus: popupState.open }}
          value={prepareDisplayDate(value?.start, dateFormat)}
          placeholder={t('components.dateRange.placeholderStart')}
          autoComplete="off"
        />
        <StyledTextField
          InputProps={{ id: `${id}-end`, onFocus: popupState.open }}
          value={prepareDisplayDate(value?.end, dateFormat)}
          placeholder={t('components.dateRange.placeholderEnd')}
          autoComplete="off"
        />
      </StyledStack>
      <CalendarPopover popupState={popupState} value={ranges} onChange={handleChange} />
    </>
  );
};

const DateRange = withFormControl<
  { start: Date | string; end: Date | string },
  { start: Date | string; end: Date | string },
  DateRangeSelectProps
>(DateRangePickerField);

export default DateRange;
